import React from "react";
import type { BatchCase } from "../../hooks/useBatchUpload";

interface BatchReviewSummaryProps {
  cases: BatchCase[];
  onRetry: (studyInstanceUid: string) => void;
  onOpenCase: (viewerId: string) => void;
  onDone: () => void;
}

/** Final batch view: successes (open in viewer) + failures (retry) + new batch. */
export const BatchReviewSummary: React.FC<BatchReviewSummaryProps> = ({
  cases,
  onRetry,
  onOpenCase,
  onDone,
}) => {
  const ready = cases.filter((c) => c.status === "ready").length;
  const failed = cases.length - ready;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-semibold text-fg">
          Batch complete — <span className="text-signal">{ready} ready</span>
          {failed > 0 && <span className="text-warn"> · {failed} failed</span>}
        </h3>
        <button
          type="button"
          onClick={onDone}
          className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim hover:text-fg"
        >
          New batch
        </button>
      </div>
      <div className="max-h-80 overflow-y-auto rounded border border-border custom-scrollbar">
        {cases.map((c) => (
          <div
            key={c.studyInstanceUid}
            className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0"
          >
            <div className="min-w-0">
              <div className="truncate font-mono text-[12px] text-fg/80">
                …{c.studyInstanceUid.slice(-16)}
              </div>
              {c.message && c.status !== "ready" && (
                <div className="truncate text-[11px] text-fg/40">{c.message}</div>
              )}
            </div>
            {c.status === "ready" && c.viewerId ? (
              <button
                type="button"
                onClick={() => onOpenCase(c.viewerId!)}
                className="flex-none rounded border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.06em] text-signal hover:border-signal-stroke-soft"
              >
                Open
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onRetry(c.studyInstanceUid)}
                className="flex-none rounded border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.06em] text-fg/70 hover:border-signal-stroke-soft"
              >
                Retry
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
